"use client";

import { useEffect, useRef, useState } from "react";

const portData = [
  {
    country: "India",
    flag: "https://customsgov.co/india.png",
    flagAlt: "Flag of India",
    count: 19,
    ports: [
      "Nhava Sheva (JNPT)", "BPT Mumbai", "Mormugao", "Haldia", "Kolkata",
      "Vishakhapatnam", "Kakinada", "Cochin", "Paradip", "Dhamra",
      "Chennai", "Tuticorin", "Ennore", "New Mangalore", "Mundra",
      "Kandla", "Pipava", "Sikka", "Hazira",
    ],
    color: "#FF9933",
    bgColor: "rgba(255,153,51,0.08)",
    borderColor: "rgba(255,153,51,0.2)",
  },
  {
    country: "UAE",
    flag: "https://customsgov.co/uae.png",
    flagAlt: "Flag of United Arab Emirates",
    count: 6,
    ports: [
      "Jebel Ali", "Al Fujayrah", "Port Khalid",
      "Port Rashid", "Khor Fakkan", "Sohar Port",
    ],
    color: "#009736",
    bgColor: "rgba(0,151,54,0.08)",
    borderColor: "rgba(0,151,54,0.2)",
  },
  {
    country: "Bangladesh",
    flag: "https://customsgov.co/bangla.png",
    flagAlt: "Flag of Bangladesh",
    count: 1,
    ports: ["Chittagong Port"],
    color: "#2196F3",
    bgColor: "rgba(33,150,243,0.08)",
    borderColor: "rgba(33,150,243,0.2)",
  },
  {
    country: "Sri Lanka",
    flag: "https://customsgov.co/srilanka.png",
    flagAlt: "Flag of Sri Lanka",
    count: 2,
    ports: ["Colombo Port", "Galle Port"],
    color: "#C8A84B",
    bgColor: "rgba(200,168,75,0.08)",
    borderColor: "rgba(200,168,75,0.2)",
  },
];

function useCountUp(target: number, duration: number = 2000, start: boolean = false) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!start) return;
    let startTime: number | null = null;
    const step = (timestamp: number) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setCount(Math.floor(eased * target));
      if (progress < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [target, duration, start]);
  return count;
}

const bigStats = [
  { value: 80, suffix: "+", label: "Ports Served", sub: "Across 4 countries" },
  { value: 2500, suffix: "+", label: "Vessels Served", sub: "Since 2016" },
  { value: 150, suffix: "+", label: "Active Clients", sub: "Worldwide" },
  { value: 97, suffix: "%", label: "Satisfaction Rate", sub: "Client reported" },
];

function BigStatItem({ stat, animate }: { stat: typeof bigStats[0]; animate: boolean }) {
  const count = useCountUp(stat.value, 2500, animate);
  return (
    <div className="text-center space-y-2 group">
      <div className="font-display text-5xl md:text-6xl font-bold text-gradient-ocean leading-none">
        {count}{stat.suffix}
      </div>
      <div className="text-white font-bold text-[15px]">{stat.label}</div>
      <div className="text-[#8BA3BC] text-[12px] uppercase tracking-widest">{stat.sub}</div>
    </div>
  );
}

export default function StatsPortsSection() {
  const sectionRef = useRef<HTMLElement>(null);
  const [visible, setVisible] = useState(false);
  const [activeCountry, setActiveCountry] = useState(0);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const active = portData[activeCountry];

  return (
    <section id="ports" ref={sectionRef} className="py-28 md:py-36 relative overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0">
        <div
          className="absolute inset-0 opacity-5"
          style={{
            backgroundImage: `radial-gradient(circle at 20% 50%, rgba(33,150,243,0.4) 0%, transparent 50%), radial-gradient(circle at 80% 50%, rgba(200,168,75,0.3) 0%, transparent 50%)`,
          }}
        />
      </div>

      <div className="relative max-w-7xl mx-auto px-6 md:px-10">
        {/* Stats Row */}
        <div
          className={`mb-24 ${visible ? "animate-fade-up" : "opacity-0"}`}
        >
          <div className="flex items-center gap-4 mb-12">
            <div className="w-8 h-px bg-ocean-500" />
            <span className="text-[11px] uppercase tracking-[0.4em] text-ocean-400 font-bold">By the Numbers</span>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 md:gap-12">
            {bigStats.map((stat, i) => (
              <BigStatItem key={i} stat={stat} animate={visible} />
            ))}
          </div>
        </div>

        <div className="section-divider mb-24" />

        {/* Port Coverage */}
        <div className={`${visible ? "animate-fade-up" : "opacity-0"}`} style={{ animationDelay: "0.3s" }}>
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
            <div className="space-y-4">
              <div className="flex items-center gap-4">
                <div className="w-8 h-px bg-gold-500" />
                <span className="text-[11px] uppercase tracking-[0.4em] text-gold-400 font-bold">Geographic Reach</span>
              </div>
              <h2 className="font-display text-4xl md:text-5xl font-bold leading-tight">
                <span className="text-white">Ports of</span>{" "}
                <span className="text-gradient-gold italic">Operation</span>
              </h2>
            </div>
            <p className="text-[#8BA3BC] max-w-sm leading-relaxed">
              80+ ports across India, UAE, Bangladesh & Sri Lanka — with 24/7 coordination support.
            </p>
          </div>

          {/* Country tabs */}
          <div className="flex flex-wrap gap-3 mb-10">
            {portData.map((country, i) => (
              <button
                key={i}
                onClick={() => setActiveCountry(i)}
                className={`flex items-center gap-3 px-5 py-3 rounded-full border transition-all duration-300 text-[13px] font-bold ${
                  activeCountry === i
                    ? "port-tab-active text-white" :"border-white/10 text-[#8BA3BC] hover:border-white/20 hover:text-white"
                }`}
              >
                <img
                  src={country.flag}
                  alt={country.flagAlt}
                  className="w-5 h-4 object-cover rounded-sm"
                />
                {country.country}
                <span
                  className="w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-black"
                  style={{
                    background: activeCountry === i ? country.bgColor : "rgba(255,255,255,0.05)",
                    color: activeCountry === i ? country.color : "#8BA3BC",
                  }}
                >
                  {country.count}
                </span>
              </button>
            ))}
          </div>

          {/* Port list */}
          <div
            className="rounded-3xl p-8 md:p-10 transition-all duration-500"
            style={{ background: active.bgColor, border: `1px solid ${active.borderColor}` }}
          >
            <div className="flex items-center gap-4 mb-8">
              <img
                src={active.flag}
                alt={active.flagAlt}
                className="w-10 h-7 object-cover rounded"
              />
              <div>
                <h3 className="text-white font-bold text-xl">{active.country}</h3>
                <p className="text-[12px] uppercase tracking-widest font-semibold" style={{ color: active.color }}>
                  {active.count} Port{active.count > 1 ? "s" : ""} Covered
                </p>
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
              {active.ports.map((port, j) => (
                <div
                  key={j}
                  className="flex items-center gap-2.5 px-4 py-2.5 rounded-xl bg-white/5 border border-white/5"
                >
                  <div
                    className="w-1.5 h-1.5 rounded-full flex-shrink-0"
                    style={{ background: active.color }}
                  />
                  <span className="text-[13px] text-white font-medium truncate">{port}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}